import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { StarRating, StarDisplayCompact } from '../components/StarRating';
import { VoiceNotePlayer } from '../components/VoiceNote';
import {
  
  ArrowLeft,
  Download,
  Edit,
  CheckCircle,
  XCircle,
  MapPin,
  Calendar,
  User,
  FileText,
  AlertCircle,
  Trash2,
  RotateCcw,
  Lock,
  Unlock,
  MessageSquare,
  Send,
  History,
  ChevronDown,
  ChevronUp,
  Archive,
  HardDrive,
  UserCheck,
  UserX,
  Clock,
  Activity,
  Volume2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: 'Draft', class: 'status-draft', icon: FileText },
  pending_review: { label: 'Pending Review', class: 'status-pending', icon: AlertCircle },
  approved: { label: 'Approved', class: 'status-approved', icon: CheckCircle },
  rejected: { label: 'Rejected', class: 'status-rejected', icon: XCircle }
};

const categoryLabels = {
  civil_works: 'Civil Works',
  electric_plumbing: 'Electric & Plumbing',
  tile_works: 'Tile Works',
  painting_works: 'Painting Works',
  doors_windows: 'Doors & Windows',
  fabrication_work: 'Fabrication Work'
};

// StarDisplay is now imported from components/StarRating.js with half-star support

export default function InspectionViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders, getEffectiveRole, isMaster } = useAuth();
  
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  
  // Revision request state
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [requestingRevision, setRequestingRevision] = useState(false);

  useEffect(() => {
    fetchInspection();
  }, [id]);

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API_URL}/inspections/${id}`, {
        headers: getAuthHeaders()
      });
      setInspection(response.data);
    } catch (error) {
      toast.error('Failed to load inspection');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;

  // Backup functions
  const handleBackupOnly = async () => {
    setBackingUp(true);
    try {
      const response = await axios.get(`${API_URL}/inspections/${id}/backup`, {
        headers: getAuthHeaders()
      });
      
      // Create and download backup file
      const backupData = response.data;
      const fileName = `Backup_${inspection.client_name.replace(/[^a-zA-Z0-9]/g, '_')}_${inspection.report_id}.json`;
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
      setShowBackupDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setBackingUp(false);
    }
  };

  const handleBackupAndDelete = async () => {
    if (!window.confirm('This will download the backup and permanently delete this report from the server. Continue?')) {
      return;
    }
    
    setBackingUp(true);
    try {
      const response = await axios.post(`${API_URL}/inspections/${id}/backup-and-delete`, {}, {
        headers: getAuthHeaders()
      });
      
      // Download backup file
      const backupData = response.data.backup;
      const fileName = `Backup_${inspection.client_name.replace(/[^a-zA-Z0-9]/g, '_')}_${inspection.report_id}.json`;
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup saved and report deleted from server');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to backup and delete');
    } finally {
      setBackingUp(false);
    }
  };

  const handleDownload = async (format = 'pdf') => {
    setDownloading(true);
    setShowDownloadMenu(false);
    try {
      const endpoint = format === 'pdf' ? 'pdf' : 'doc';
      const response = await axios.get(`${API_URL}/inspections/${id}/${endpoint}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON) instead of file
      if (response.data.type === 'application/json') {
        const errorText = await response.data.text();
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.detail || 'Download failed');
      }
      
      // Create blob with correct MIME type
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blob = new Blob([response.data], { type: mimeType });
      
      const extension = format === 'pdf' ? 'pdf' : 'docx';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QC_Report_${inspection.report_id}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error('Download error:', error);
      let errorMessage = `Failed to download ${format.toUpperCase()}`;
      
      // Handle blob error response - axios returns blob even on error when responseType is 'blob'
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch (parseError) {
          // Couldn't parse blob as JSON, use default message
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const handleLock = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/lock`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Report locked for review');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock report');
    }
  };

  const handleUnlock = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/unlock`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Report released');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock report');
    }
  };

  const handleSendComment = async () => {
    if (!newComment.trim()) return;
    
    setSendingComment(true);
    try {
      await axios.post(`${API_URL}/inspections/${id}/comments`, 
        { message: newComment },
        { headers: getAuthHeaders() }
      );
      setNewComment('');
      toast.success('Comment sent');
      fetchInspection();
    } catch (error) {
      toast.error('Failed to send comment');
    } finally {
      setSendingComment(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/approve`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Inspection approved');
      fetchInspection();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/reject`, { notes: rejectNotes }, {
        headers: getAuthHeaders()
      });
      toast.success('Inspection rejected');
      setShowRejectDialog(false);
      fetchInspection();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionNotes.trim()) {
      toast.error('Please provide revision notes');
      return;
    }
    
    setRequestingRevision(true);
    try {
      await axios.post(`${API_URL}/inspections/${id}/request-revision`, 
        { notes: revisionNotes }, 
        { headers: getAuthHeaders() }
      );
      toast.success('Revision requested - QC Engineer notified');
      setShowRevisionDialog(false);
      setRevisionNotes('');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    } finally {
      setRequestingRevision(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/inspections/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Inspection deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleRecall = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/recall`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Report recalled for editing');
      fetchInspection();
    } catch (error) {
      toast.error('Failed to recall report');
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await axios.post(`${API_URL}/inspections/${id}/reopen`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Report reopened for editing');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen report');
    } finally {
      setReopening(false);
    }
  };

  const handleAllowQCEdit = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/allow-qc-edit`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('QC Engineer can now edit this report');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant permission');
    }
  };

  const handleRevokeQCEdit = async () => {
    try {
      await axios.post(`${API_URL}/inspections/${id}/revoke-qc-edit`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('QC Engineer edit permission revoked');
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke permission');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!inspection) {
    return null;
  }

  const StatusIcon = statusConfig[inspection.status]?.icon || FileText;
  const isCreator = user?.id === inspection.created_by;
  const isReviewer = effectiveRole === 'review_engineer';
  const isLockedByMe = inspection.locked_by === user?.id;
  const isLockedByOther = inspection.locked_by && !isLockedByMe;
  
  // QC Engineer can edit their own drafts/rejected reports
  const canEditAsQC = effectiveRole === 'field_qc_engineer' && isCreator && ['draft', 'rejected'].includes(inspection.status);
  // Reviewer can edit if they have locked the report
  const canEditAsReviewer = isReviewer && isLockedByMe && inspection.status === 'pending_review';
  const canEdit = canEditAsQC || canEditAsReviewer;
  
  const canRecall = effectiveRole === 'field_qc_engineer' && isCreator && inspection.status === 'pending_review' && !inspection.locked_by;
  const canReview = isReviewer && inspection.status === 'pending_review';
  const canLock = isReviewer && inspection.status === 'pending_review' && !inspection.locked_by;
  const canUnlock = isLockedByMe || (isMaster && isMaster());
  const canComment = (isCreator || isLockedByMe) && ['pending_review', 'draft'].includes(inspection.status);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Mobile Optimized */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Top row - Navigation and Report ID */}
          <div className="flex items-center justify-between h-12 sm:h-14 border-b border-slate-700/50">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white p-1 sm:p-2"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img 
                  src="/mypropmate-logo.png" 
                  alt="MyPropMate Logo" 
                  className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
                />
                <span className="text-sm sm:text-lg font-heading font-bold truncate max-w-[120px] sm:max-w-none">
                  {inspection.report_id}
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2">
              <span className={`status-pill ${statusConfig[inspection.status]?.class} text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5`}>
                <StatusIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                <span className="hidden sm:inline">{statusConfig[inspection.status]?.label}</span>
                <span className="sm:hidden">{statusConfig[inspection.status]?.label.split(' ')[0]}</span>
              </span>
              {inspection.locked_by && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-orange-500/20 text-orange-400 text-[10px] sm:text-xs rounded-full">
                  <Lock className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline" />
                  <span className="hidden sm:inline ml-1">{isLockedByMe ? 'Locked by you' : `Locked`}</span>
                </span>
              )}
            </div>
          </div>
          
          {/* Bottom row - Action buttons (scrollable on mobile) */}
          <div className="flex items-center gap-1.5 sm:gap-2 h-12 sm:h-14 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
            {/* Lock/Unlock buttons for reviewers */}
            {canLock && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLock}
                className="border-orange-500 text-orange-400 hover:bg-orange-500/20 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
              >
                <Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Take for Review</span>
                <span className="sm:hidden">Take</span>
              </Button>
            )}
            {canUnlock && inspection.locked_by && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnlock}
                className="border-green-500 text-green-400 hover:bg-green-500/20 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
              >
                <Unlock className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Release
              </Button>
            )}
            
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/inspection/${id}/edit`)}
                  className="border-slate-600 text-white hover:bg-slate-800 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                  data-testid="edit-btn"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Edit
                </Button>
                {canRecall && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecall}
                    className="border-orange-500 text-orange-400 hover:bg-orange-500/20 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                    data-testid="recall-btn"
                  >
                    <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Recall
                  </Button>
                )}
                {inspection.status !== 'pending_review' && effectiveRole === 'field_qc_engineer' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-400 hover:text-red-300 hover:bg-slate-800 px-2 h-8 sm:h-9 flex-shrink-0"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                )}
              </>
            )}
            
            {canReview && isLockedByMe && (
              <>
                <Button
                  size="sm"
                  onClick={handleApprove}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                  data-testid="approve-btn"
                >
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowRevisionDialog(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                  data-testid="request-revision-btn"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Request Changes
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowRejectDialog(true)}
                  className="text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                  data-testid="reject-btn"
                >
                  <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Reject
                </Button>
              </>
            )}
            
            {/* Download Options - Only for Reviewers and Master users */}
            {(isReviewer || (isMaster && isMaster())) && (
              <div className="relative flex-shrink-0">
                <Button
                  size="sm"
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  disabled={downloading}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap"
                  data-testid="download-btn"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  {downloading ? '...' : 'Download'}
                  <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Button>
                {showDownloadMenu && !downloading && (
                  <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-white border border-slate-200 rounded-md shadow-lg z-50">
                    <button
                      onClick={() => handleDownload('pdf')}
                      className="flex items-center w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-slate-700 hover:bg-slate-100"
                      data-testid="download-pdf-option"
                    >
                      <FileText className="w-4 h-4 mr-2 text-red-500" />
                      Download as PDF
                    </button>
                    <button
                      onClick={() => handleDownload('doc')}
                      className="flex items-center w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-slate-700 hover:bg-slate-100"
                      data-testid="download-doc-option"
                    >
                      <FileText className="w-4 h-4 mr-2 text-blue-500" />
                      Download as DOC
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Edit button for approved reports - Reviewers and Master can always edit */}
            {inspection.status === 'approved' && (isReviewer || (isMaster && isMaster())) && (
              <Button
                onClick={() => navigate(`/inspection/${id}/edit`)}
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                data-testid="edit-approved-btn"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Edit Report</span>
                <span className="sm:hidden">Edit</span>
              </Button>
            )}
            
            {/* QC Edit Permission Toggle - Only for Reviewers and Master on approved reports */}
            {inspection.status === 'approved' && (isReviewer || (isMaster && isMaster())) && (
              <>
                {inspection.qc_edit_allowed ? (
                  <Button
                    onClick={handleRevokeQCEdit}
                    variant="outline"
                    size="sm"
                    className="border-orange-500 text-orange-600 hover:bg-orange-50 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                    data-testid="revoke-qc-edit-btn"
                  >
                    <UserX className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">Revoke QC Edit</span>
                    <span className="sm:hidden">Revoke</span>
                  </Button>
                ) : (
                  <Button
                    onClick={handleAllowQCEdit}
                    variant="outline"
                    size="sm"
                    className="border-green-500 text-green-600 hover:bg-green-50 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                    data-testid="allow-qc-edit-btn"
                  >
                    <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">Allow QC Edit</span>
                    <span className="sm:hidden">Allow</span>
                  </Button>
                )}
              </>
            )}
            
            {/* Edit button for QC Engineer on approved reports (if permission granted) */}
            {inspection.status === 'approved' && isCreator && inspection.qc_edit_allowed && (
              <Button
                onClick={() => navigate(`/inspection/${id}/edit`)}
                variant="outline"
                size="sm"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                data-testid="qc-edit-approved-btn"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Edit Report</span>
                <span className="sm:hidden">Edit</span>
              </Button>
            )}
            
            {/* Backup button for approved reports - Only for Reviewers and Master users */}
            {inspection.status === 'approved' && (isReviewer || (isMaster && isMaster())) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBackupDialog(true)}
                className="border-purple-500 text-purple-400 hover:bg-purple-500/20 text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 whitespace-nowrap flex-shrink-0"
                data-testid="backup-btn"
              >
                <Archive className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Backup
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - Mobile Optimized */}
      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* Lock notification for QC Engineer */}
        {isCreator && inspection.locked_by && !isLockedByMe && (
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
            <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-orange-800 text-sm sm:text-base">Report Under Review</h3>
              <p className="text-orange-700 text-xs sm:text-sm">
                {inspection.locked_by_name} has taken this report for review. 
                You can communicate with them using the comments section below.
              </p>
            </div>
          </div>
        )}

        {/* Locked by other reviewer notification */}
        {isReviewer && isLockedByOther && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
            <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 text-sm sm:text-base">Report Locked</h3>
              <p className="text-red-700 text-xs sm:text-sm">
                This report is currently being reviewed by {inspection.locked_by_name}. 
                You cannot edit or take action on this report.
              </p>
            </div>
          </div>
        )}

        {/* Reviewer Notes */}
        {inspection.status === 'rejected' && inspection.reviewer_notes && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <h3 className="font-semibold text-red-800 mb-2">Rejection Notes</h3>
            <p className="text-red-700">{inspection.reviewer_notes}</p>
          </div>
        )}

        {/* Revision Request Notes - Show when revision was requested */}
        {inspection.revision_requested && inspection.revision_notes && (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <Edit className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-amber-800">Revision Requested</h3>
                  {inspection.revision_requested_at && (
                    <span className="text-xs text-amber-600">
                      {new Date(inspection.revision_requested_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className="text-amber-700 whitespace-pre-wrap">{inspection.revision_notes}</p>
                {inspection.revision_requested_by_name && (
                  <p className="text-xs text-amber-600 mt-2">
                    Requested by: {inspection.revision_requested_by_name}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Change Log Section */}
        {inspection.change_log && inspection.change_log.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <button
              onClick={() => setShowChangeLog(!showChangeLog)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Reviewer Changes ({inspection.change_log.length})
                </h2>
              </div>
              {showChangeLog ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showChangeLog && (
              <div className="border-t border-slate-200 p-4 space-y-3 max-h-64 overflow-y-auto">
                {inspection.change_log.map((change, index) => (
                  <div key={change.id || index} className="p-3 bg-blue-50 rounded-sm text-sm">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-semibold text-blue-800">{change.field_name}</span>
                      <span className="text-xs text-slate-500">{formatDate(change.changed_at)}</span>
                    </div>
                    <p className="text-slate-600">
                      <span className="line-through text-red-600">{change.old_value}</span>
                      {' → '}
                      <span className="text-green-600 font-medium">{change.new_value}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Changed by {change.changed_by_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Audit Trail Section - Shows all inspection actions/history */}
        {inspection.audit_trail && inspection.audit_trail.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <button
              onClick={() => setShowAuditTrail(!showAuditTrail)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-500" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Activity Log ({inspection.audit_trail.length})
                </h2>
              </div>
              {showAuditTrail ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showAuditTrail && (
              <div className="border-t border-slate-200 p-4 space-y-3 max-h-80 overflow-y-auto">
                {inspection.audit_trail.slice().reverse().map((entry, index) => {
                  const actionConfig = {
                    'created': { label: 'Created', icon: FileText, color: 'text-green-600 bg-green-50' },
                    'updated': { label: 'Updated', icon: Edit, color: 'text-blue-600 bg-blue-50' },
                    'status_changed': { label: 'Status Changed', icon: AlertCircle, color: 'text-orange-600 bg-orange-50' },
                    'submitted': { label: 'Submitted for Review', icon: Send, color: 'text-cyan-600 bg-cyan-50' },
                    'approved': { label: 'Approved', icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50' },
                    'rejected': { label: 'Rejected', icon: XCircle, color: 'text-red-600 bg-red-50' },
                    'recalled': { label: 'Recalled', icon: RotateCcw, color: 'text-amber-600 bg-amber-50' },
                    'locked': { label: 'Locked for Review', icon: Lock, color: 'text-purple-600 bg-purple-50' },
                    'unlocked': { label: 'Unlocked', icon: Unlock, color: 'text-gray-600 bg-gray-50' }
                  };
                  const config = actionConfig[entry.action] || { label: entry.action, icon: Clock, color: 'text-slate-600 bg-slate-50' };
                  const ActionIcon = config.icon;
                  
                  return (
                    <div key={index} className={`p-3 rounded-sm text-sm ${config.color}`}>
                      <div className="flex items-start gap-3">
                        <ActionIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <span className="font-semibold">{config.label}</span>
                            <span className="text-xs opacity-75">{formatDate(entry.timestamp)}</span>
                          </div>
                          <p className="text-xs mt-1 opacity-90">by {entry.user_name}</p>
                          {entry.details && (
                            <div className="mt-2 text-xs opacity-80">
                              {entry.details.old_status && entry.details.new_status && (
                                <span>
                                  <span className="line-through">{entry.details.old_status?.replace('_', ' ')}</span>
                                  {' → '}
                                  <span className="font-medium">{entry.details.new_status?.replace('_', ' ')}</span>
                                </span>
                              )}
                              {entry.details.fields_updated && entry.details.fields_updated.length > 0 && (
                                <span>Updated: {entry.details.fields_updated.join(', ')}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Comments Section */}
        {(canComment || (inspection.comments && inspection.comments.length > 0)) && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <button
              onClick={() => setShowComments(!showComments)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-500" />
                <h2 className="text-lg font-semibold text-slate-900">
                  Comments ({inspection.comments?.length || 0})
                </h2>
              </div>
              {showComments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showComments && (
              <div className="border-t border-slate-200">
                {/* Comments list */}
                <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
                  {(!inspection.comments || inspection.comments.length === 0) ? (
                    <p className="text-slate-500 text-center py-4">No comments yet</p>
                  ) : (
                    inspection.comments.map((comment, index) => (
                      <div 
                        key={comment.id || index} 
                        className={`p-3 rounded-sm ${
                          comment.user_id === user?.id 
                            ? 'bg-cyan-50 ml-8' 
                            : 'bg-slate-50 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-semibold text-sm text-slate-800">
                            {comment.user_name}
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              ({comment.user_role === 'field_qc_engineer' ? 'QC Engineer' : 'Reviewer'})
                            </span>
                          </span>
                          <span className="text-xs text-slate-500">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-slate-700 text-sm">{comment.message}</p>
                      </div>
                    ))
                  )}
                </div>
                {/* New comment input */}
                {canComment && (
                  <div className="border-t border-slate-200 p-4">
                    <div className="flex gap-2">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && handleSendComment()}
                      />
                      <Button 
                        onClick={handleSendComment} 
                        disabled={sendingComment || !newComment.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cover Info */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Project Information</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Client Name</p>
              <p className="font-semibold text-slate-900">{inspection.client_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Project Name</p>
              <p className="font-semibold text-slate-900">{inspection.project_name}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-sm text-slate-500 mb-1">Property Address</p>
              <p className="font-semibold text-slate-900 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                {inspection.property_address}
              </p>
              {/* GPS Location */}
              {inspection.gps_location && (
                <a 
                  href={`https://www.google.com/maps?q=${inspection.gps_location.latitude},${inspection.gps_location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-xs text-cyan-600 hover:text-cyan-700 hover:underline"
                >
                  <MapPin className="w-3 h-3" />
                  View on Map ({inspection.gps_location.latitude?.toFixed(6)}, {inspection.gps_location.longitude?.toFixed(6)})
                </a>
              )}
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500 mb-1">Date of Inspection</p>
              <p className="font-semibold text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
                {inspection.date_of_inspection}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500 mb-1">QC Engineer</p>
              <p className="font-semibold text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <User className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
                {inspection.qc_engineer_name || inspection.created_by_name}
              </p>
            </div>
          </div>
        </div>

        {/* Overall Rating with Property Photo - Mobile Optimized */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Overall Rating */}
            <div className="text-center sm:text-left">
              <h2 className="text-lg sm:text-xl font-heading font-bold text-slate-900 mb-3 sm:mb-4">Overall Rating</h2>
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <StarDisplayCompact rating={inspection.overall_rating} size="sm" />
              </div>
              <span className="text-2xl sm:text-3xl font-bold text-slate-900">{inspection.overall_rating}/10</span>
              <p className="text-xs sm:text-sm font-semibold mt-2 text-slate-800">
                {inspection.overall_rating >= 8 ? 'EXCELLENT' : 
                 inspection.overall_rating >= 5.5 ? 'GOOD' : 
                 inspection.overall_rating >= 3.5 ? 'AVERAGE' : 'POOR'}
              </p>
            </div>
            
            {/* Property Photo */}
            <div>
              <h2 className="text-lg sm:text-xl font-heading font-bold text-slate-900 mb-3 sm:mb-4">Property Photo</h2>
              {inspection.property_photo ? (
                <div className="border-2 border-amber-500 rounded-sm overflow-hidden">
                  {inspection.view_360_link ? (
                    <a 
                      href={inspection.view_360_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block cursor-pointer hover:opacity-90 transition-opacity active:opacity-80"
                      title="Click for 360° View"
                    >
                      <img 
                        src={inspection.property_photo} 
                        alt="Property" 
                        className="w-full h-40 sm:h-48 object-cover"
                      />
                      <p className="text-center text-xs sm:text-sm text-blue-600 font-medium py-2 bg-slate-50 hover:bg-slate-100">
                        Click Photo for 360° View
                      </p>
                    </a>
                  ) : (
                    <img 
                      src={inspection.property_photo} 
                      alt="Property" 
                      className="w-full h-40 sm:h-48 object-cover"
                    />
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-300 rounded-sm h-40 sm:h-48 flex items-center justify-center bg-slate-50">
                  <p className="text-slate-400 text-sm">No property photo available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Category Ratings - Mobile Optimized */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-heading font-bold text-slate-900 mb-3 sm:mb-4">Category Ratings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                <span className="font-medium text-slate-700">{label}</span>
                <StarDisplayCompact rating={inspection.category_ratings[key] || 5} />
              </div>
            ))}
          </div>
        </div>

        {/* Area Ratings */}
        {inspection.area_ratings?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Area-wise Ratings</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULAR</th>
                    <th>RATING</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.area_ratings.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.area_name}</td>
                      <td><StarDisplayCompact rating={item.rating} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Area Statement */}
        {inspection.area_measurements?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Area Statement</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULAR</th>
                    <th>LENGTH (FT)</th>
                    <th>BREADTH (FT)</th>
                    <th>AREA (SFT)</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.area_measurements.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.particular}</td>
                      <td>{item.length || '-'}</td>
                      <td>{item.breadth || '-'}</td>
                      <td>{item.area || '-'}</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-slate-50">
                    <td></td>
                    <td>TOTAL CARPET AREA</td>
                    <td></td>
                    <td></td>
                    <td>{inspection.area_measurements.reduce((sum, m) => sum + (m.area || 0), 0)} SFT</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {(inspection.area_statement_notes || inspection.area_statement_voice_note) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                {inspection.area_statement_notes && (
                  <>
                    <p className="text-sm font-semibold text-slate-700">Notes:</p>
                    <p className="text-slate-600 whitespace-pre-wrap mt-1">{inspection.area_statement_notes}</p>
                  </>
                )}
                {inspection.area_statement_voice_note && (
                  <div className="mt-2">
                    <VoiceNotePlayer audioData={inspection.area_statement_voice_note} label="Voice Note" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Doors & Windows */}
        {inspection.doors_windows?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Doors & Windows Statement</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULAR</th>
                    <th>WIDTH (FT)</th>
                    <th>HEIGHT (FT)</th>
                    <th>OPENING (SFT)</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.doors_windows.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.particular}</td>
                      <td>{item.width || '-'}</td>
                      <td>{item.height || '-'}</td>
                      <td>{item.opening || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(inspection.doors_windows_notes || inspection.doors_windows_voice_note) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                {inspection.doors_windows_notes && (
                  <>
                    <p className="text-sm font-semibold text-slate-700">Notes:</p>
                    <p className="text-slate-600 whitespace-pre-wrap mt-1">{inspection.doors_windows_notes}</p>
                  </>
                )}
                {inspection.doors_windows_voice_note && (
                  <div className="mt-2">
                    <VoiceNotePlayer audioData={inspection.doors_windows_voice_note} label="Voice Note" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Brand & Type Statement */}
        {inspection.brand_types?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Brand & Type Statement</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULAR</th>
                    <th>BRAND</th>
                    <th>TYPE</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.brand_types.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.particular}</td>
                      <td>{item.brand}</td>
                      <td>{item.type_spec}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(inspection.brand_type_notes || inspection.brand_type_voice_note) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                {inspection.brand_type_notes && (
                  <>
                    <p className="text-sm font-semibold text-slate-700">Notes:</p>
                    <p className="text-slate-600 whitespace-pre-wrap mt-1">{inspection.brand_type_notes}</p>
                  </>
                )}
                {inspection.brand_type_voice_note && (
                  <div className="mt-2">
                    <VoiceNotePlayer audioData={inspection.brand_type_voice_note} label="Voice Note" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Dimension Statement */}
        {inspection.dimensions?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Dimension Statement</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULAR</th>
                    <th>DIMENSION</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.dimensions.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.particular}</td>
                      <td>{item.dimension}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inspection.dimension_notes && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700">Notes:</p>
                <p className="text-slate-600 whitespace-pre-wrap mt-1">{inspection.dimension_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Agreement vs Actual */}
        {inspection.agreement_comparisons?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Agreement Specification vs Actual Work</h2>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SL NO</th>
                    <th>PARTICULARS</th>
                    <th>AS PER AGREEMENT</th>
                    <th>ACTUAL FINDING</th>
                  </tr>
                </thead>
                <tbody>
                  {inspection.agreement_comparisons.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.particulars}</td>
                      <td>{item.as_per_agreement}</td>
                      <td>{item.actual_finding}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {inspection.agreement_comparison_notes && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700">Notes:</p>
                <p className="text-slate-600 whitespace-pre-wrap mt-1">{inspection.agreement_comparison_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Area Defects Section */}
        {inspection.area_defects?.length > 0 && inspection.area_defects.some(area => area.defects?.length > 0) && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Defects & Issues
            </h2>
            <div className="space-y-4">
              {inspection.area_defects.map((area, areaIndex) => (
                area.defects?.length > 0 && (
                  <div key={areaIndex} className="border border-slate-200 rounded-sm">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-800">{area.area_name}</h3>
                    </div>
                    <div className="p-4 space-y-3">
                      {area.defects.map((defect, defectIndex) => (
                        <div key={defectIndex} className="bg-red-50 border border-red-200 rounded-sm p-3">
                          <p className="text-slate-700">{defect.defect}</p>
                          
                          {/* Voice Note for Defect */}
                          {defect.voice_note && (
                            <div className="mt-2">
                              <VoiceNotePlayer 
                                audioData={defect.voice_note} 
                                label={`Defect ${defectIndex + 1} Voice Note`}
                              />
                            </div>
                          )}
                          
                          {/* Defect Photos */}
                          {(defect.image || defect.image2) && (
                            <div className="mt-3 flex gap-2 flex-wrap">
                              {defect.image && (
                                <img 
                                  src={defect.image} 
                                  alt="Defect photo 1"
                                  className="w-24 h-24 object-cover rounded-sm border border-slate-200"
                                />
                              )}
                              {defect.image2 && (
                                <img 
                                  src={defect.image2} 
                                  alt="Defect photo 2"
                                  className="w-24 h-24 object-cover rounded-sm border border-slate-200"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Conclusion */}
        {inspection.conclusion && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-700 whitespace-pre-wrap">{inspection.conclusion}</p>
            {/* Voice Note for Conclusion */}
            {inspection.conclusion_voice_note && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <VoiceNotePlayer 
                  audioData={inspection.conclusion_voice_note} 
                  label="Conclusion Voice Note"
                />
              </div>
            )}
          </div>
        )}

        {/* Custom Fields */}
        {inspection.custom_fields?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Additional Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {inspection.custom_fields.map((field, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-sm text-slate-500">{field.field_name}</p>
                  <p className="font-semibold text-slate-900">{field.field_value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Inspection</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">Rejection Notes</label>
            <Textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Explain the reason for rejection..."
              className="mt-2"
              rows={4}
              data-testid="reject-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-500" />
              Request Changes from QC Engineer
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              This will send the report back to the QC Engineer for revisions. 
              They will be notified and can make changes based on your feedback.
            </p>
            <label className="text-sm font-medium text-slate-700">Revision Notes *</label>
            <Textarea
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder="Describe what changes are needed...&#10;&#10;• Point 1&#10;• Point 2&#10;• Point 3"
              className="mt-2"
              rows={6}
              data-testid="revision-notes-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Be specific about what needs to be corrected or improved.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestRevision} 
              disabled={requestingRevision || !revisionNotes.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="confirm-revision-btn"
            >
              {requestingRevision ? 'Sending...' : 'Request Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Inspection</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-slate-600">
            Are you sure you want to delete this inspection? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Dialog */}
      <Dialog open={showBackupDialog} onOpenChange={setShowBackupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-purple-500" />
              Backup Inspection Report
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-600 text-sm">
              Create a backup of this approved report. You can restore it later if needed.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <p className="text-sm font-medium text-slate-900 mb-1">{inspection?.client_name}</p>
              <p className="text-xs text-slate-500">Report ID: {inspection?.report_id}</p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={handleBackupOnly}
                disabled={backingUp}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="backup-only-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {backingUp ? 'Creating Backup...' : 'Download Backup File'}
              </Button>
              <Button
                onClick={handleBackupAndDelete}
                disabled={backingUp}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                data-testid="backup-delete-btn"
              >
                <HardDrive className="w-4 h-4 mr-2" />
                {backingUp ? 'Processing...' : 'Backup & Delete from Server'}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                The second option will free up server space by removing the report after backup.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBackupDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
